/**
 * Marketplace Query Utilities for Edition-Based System
 * Handles querying master pieces with aggregated edition data
 * OPTIMIZED VERSION - Uses database views and batch queries
 */

import { supabase } from '../lib/supabase';
import { normalizePieces } from './pieceNormalizer.js';

/**
 * Get all marketplace pieces (masters only) with edition aggregation
 * OPTIMIZED: Uses marketplace_pieces view for single-query aggregation
 * @param {number} page - Page number (0-based)
 * @param {number} limit - Number of pieces per page
 * @returns {Promise<Object>} { pieces, total, page, limit, totalPages }
 */
export async function getMarketplacePieces(page = 0, limit = 20) {
  try {
    console.log('🔍 getMarketplacePieces: Fetching master pieces with page:', page, 'limit:', limit);
    const from = page * limit;
    const to = from + limit - 1;

    // Query pieces table DIRECTLY (like activity page) - view was dropping columns
    // Use 'planned' count for better performance with thousands of records
    const { data, error, count } = await supabase
      .from('pieces')
      .select(`
        *,
        social_links,
        creators (
          id,
          wallet,
          handle,
          is_verified,
          verification_type,
          profile_picture_url,
          banner_url,
          bio,
          social_links,
          total_pieces_created
        )
      `, { count: 'planned' })
      .eq('is_public', true)
      .eq('is_draft', false)
      .not('mint_address', 'is', null)
      .order('created_at', { ascending: false })
      .range(from, to);

    console.log('📊 Query result:', { dataLength: data?.length, error, count });
    
    if (error) {
      console.error('❌ Error getting marketplace pieces:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      console.warn('⚠️ No pieces returned from marketplace_pieces view!');
      console.warn('   This could be an RLS (Row Level Security) issue.');
      console.warn('   Count from query:', count);
    }

    // Extract unique holder wallets for batch query
    const holderWallets = [...new Set(data.map(p => p.holder_wallet).filter(Boolean))];
    
    // Batch fetch all holder info in ONE query
    const holderMap = await batchGetHolderInfo(holderWallets);

    // Enhance pieces with holder data
    const enhancedPieces = data.map(piece => ({
      ...piece,
      lowestPrice: piece.listing_price || piece.price,
      availableCount: 0,  // TODO: Calculate from editions
      holder: piece.holder_wallet ? holderMap[piece.holder_wallet] : null
    }));

    // Normalize pieces to ensure proper URL generation from bucket/key
    const normalizedPieces = normalizePieces(enhancedPieces);
    
    console.log('✅ Found', normalizedPieces.length, 'marketplace pieces');

    return {
      pieces: normalizedPieces,
      total: count || 0,
      page,
      limit,
      totalPages: Math.ceil((count || 0) / limit)
    };

  } catch (error) {
    console.error('❌ Error in getMarketplacePieces:', error);
    throw error;
  }
}

/**
 * Batch get holder information for multiple wallets
 * OPTIMIZED: Single query instead of N queries
 * @param {string[]} walletAddresses - Array of wallet addresses
 * @returns {Promise<Object>} Map of wallet -> holder info
 */
async function batchGetHolderInfo(walletAddresses) {
  if (!walletAddresses || walletAddresses.length === 0) {
    return {};
  }

  try {
    const { data: holders } = await supabase
      .from('creators')
      .select('id, wallet, handle, is_verified, verification_type, profile_picture_url, banner_url, bio, social_links, total_pieces_created')
      .in('wallet', walletAddresses);
    
    // Convert array to map for O(1) lookup
    const holderMap = {};
    (holders || []).forEach(holder => {
      holderMap[holder.wallet] = holder;
    });
    
    return holderMap;
  } catch (error) {
    console.error('Error batch fetching holder info:', error);
    return {};
  }
}

/**
 * Get pieces by category (masters only)
 * OPTIMIZED: Uses marketplace_pieces view
 * @param {string} category - Piece category
 * @param {number} page - Page number (0-based)
 * @param {number} limit - Number of pieces per page
 * @returns {Promise<Object>} { pieces, total, page, limit, totalPages }
 */
export async function getMarketplacePiecesByCategory(category, page = 0, limit = 20) {
  try {
    const from = page * limit;
    const to = from + limit - 1;

    const { data, error, count } = await supabase
      .from('marketplace_pieces')
      .select(`
        *,
        social_links,
        creators (
          id,
          wallet,
          handle,
          is_verified,
          verification_type,
          profile_picture_url,
          banner_url,
          bio,
          social_links,
          total_pieces_created
        )
      `, { count: 'planned' })
      .eq('category', category)
      .neq('listing_status', 'draft')  // Show all pieces except drafts
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) {
      console.error('Error getting pieces by category:', error);
      throw error;
    }

    // Batch fetch holder info
    const holderWallets = [...new Set(data.map(p => p.holder_wallet).filter(Boolean))];
    const holderMap = await batchGetHolderInfo(holderWallets);

    const enhancedPieces = data.map(piece => ({
      ...piece,
      lowestPrice: piece.lowest_price,
      availableCount: piece.current_available_count,
      holder: piece.holder_wallet ? holderMap[piece.holder_wallet] : null
    }));

    // Normalize pieces to ensure proper URL generation from bucket/key
    const normalizedPieces = normalizePieces(enhancedPieces);

    return {
      pieces: normalizedPieces,
      total: count || 0,
      page,
      limit,
      totalPages: Math.ceil((count || 0) / limit)
    };

  } catch (error) {
    console.error('Error in getMarketplacePiecesByCategory:', error);
    throw error;
  }
}

/**
 * Search marketplace pieces (masters only)
 * OPTIMIZED: Uses marketplace_pieces view with planned count for performance
 * @param {string} query - Search query
 * @param {number} page - Page number (0-based)
 * @param {number} limit - Number of pieces per page
 * @returns {Promise<Object>} { pieces, total, page, limit, totalPages }
 */
export async function searchMarketplacePieces(query, page = 0, limit = 100) {
  try {
    const from = page * limit;
    const to = from + limit - 1;

    // Use 'planned' count for better performance with large datasets
    // This uses PostgreSQL's query planner estimate instead of exact count
    // Search ALL minted pieces (consistent with marketplace filters)
    const { data, error, count} = await supabase
      .from('marketplace_pieces')
      .select(`
        *,
        social_links,
        creators (
          id,
          wallet,
          handle,
          is_verified,
          verification_type,
          profile_picture_url,
          banner_url,
          bio,
          social_links,
          total_pieces_created
        )
      `, { count: 'planned' })
      .eq('is_public', true)           // Only public pieces
      .eq('is_draft', false)           // No drafts
      .not('mint_address', 'is', null) // Only minted pieces (actually created on blockchain)
      .or(`name.ilike.%${query}%,description.ilike.%${query}%,brand.ilike.%${query}%`)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) {
      console.error('Error searching marketplace pieces:', error);
      throw error;
    }

    // Batch fetch holder info
    const holderWallets = [...new Set(data.map(p => p.holder_wallet).filter(Boolean))];
    const holderMap = await batchGetHolderInfo(holderWallets);

    const enhancedPieces = data.map(piece => ({
      ...piece,
      lowestPrice: piece.lowest_price,
      availableCount: piece.current_available_count,
      holder: piece.holder_wallet ? holderMap[piece.holder_wallet] : null
    }));

    // Normalize pieces to ensure proper URL generation from bucket/key
    const normalizedPieces = normalizePieces(enhancedPieces);

    return {
      pieces: normalizedPieces,
      total: count || 0,
      page,
      limit,
      totalPages: Math.ceil((count || 0) / limit)
    };

  } catch (error) {
    console.error('Error in searchMarketplacePieces:', error);
    throw error;
  }
}

/**
 * Get pieces by creator (masters only, public pieces)
 * OPTIMIZED: Uses marketplace_pieces view
 * @param {string} walletAddress - Creator's wallet address
 * @param {number} page - Page number (0-based)
 * @param {number} limit - Number of pieces per page
 * @returns {Promise<Object>} { pieces, total, page, limit, totalPages }
 */
export async function getMarketplacePiecesByCreator(walletAddress, page = 0, limit = 20) {
  try {
    console.log('🔍 Getting marketplace pieces by creator:', walletAddress);
    
    // First, get the creator ID from the wallet
    const { data: creator, error: creatorError } = await supabase
      .from('creators')
      .select('id')
      .eq('wallet', walletAddress)
      .maybeSingle();

    if (creatorError || !creator) {
      console.log('⚠️ Creator not found for wallet:', walletAddress);
      return {
        pieces: [],
        total: 0,
        page,
        limit,
        totalPages: 0
      };
    }

    const from = page * limit;
    const to = from + limit - 1;

    // Query pieces table DIRECTLY (like Explore page) to get ALL pieces created by this creator
    // This ensures we show all pieces regardless of holder, so the frontend grouping works correctly
    // Use 'planned' count for better performance with large datasets
    const { data, error, count } = await supabase
      .from('pieces')
      .select(`
        *,
        social_links,
        creators (
          id,
          wallet,
          handle,
          is_verified,
          verification_type,
          profile_picture_url,
          banner_url,
          bio,
          social_links,
          total_pieces_created
        )
      `, { count: 'planned' })
      .eq('creator_id', creator.id)
      .eq('is_public', true)
      .eq('is_draft', false)
      .not('mint_address', 'is', null)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) {
      console.error('Error getting pieces by creator:', error);
      throw error;
    }

    // Batch fetch holder info
    const holderWallets = [...new Set(data.map(p => p.holder_wallet).filter(Boolean))];
    const holderMap = await batchGetHolderInfo(holderWallets);

    const enhancedPieces = data.map(piece => ({
      ...piece,
      lowestPrice: piece.listing_price || piece.price,
      availableCount: 0,  // Frontend grouping will handle this
      holder: piece.holder_wallet ? holderMap[piece.holder_wallet] : null
    }));

    // Normalize pieces to ensure proper URL generation from bucket/key
    const normalizedPieces = normalizePieces(enhancedPieces);

    console.log('✅ Found', normalizedPieces.length, 'pieces by creator (will be grouped on frontend)');

    return {
      pieces: normalizedPieces,
      total: count || 0,
      page,
      limit,
      totalPages: Math.ceil((count || 0) / limit)
    };

  } catch (error) {
    console.error('Error in getMarketplacePiecesByCreator:', error);
    throw error;
  }
}

/**
 * Get trending pieces (masters only)
 * OPTIMIZED: Reduced queries and simplified logic
 * @param {number} limit - Number of pieces to return
 * @returns {Promise<Array>} Array of trending pieces
 */
export async function getTrendingPieces(limit = 100) {
  try {
    console.log('🔥 Getting trending pieces, limit:', limit);

    // Use the trending_pieces view if it exists, otherwise query pieces directly
    const { data: trendingData, error: trendingError } = await supabase
      .from('trending_pieces')
      .select('*')
      .limit(limit);

    if (trendingError || !trendingData || trendingData.length === 0) {
      console.log('⚠️ Trending view not available or empty, falling back to regular query');
      
      // Fallback: Query pieces directly
      const { data, error } = await supabase
        .from('pieces')
        .select(`
          *,
          social_links,
          creators (
            id,
            wallet,
            handle,
            is_verified,
            verification_type,
            profile_picture_url,
            banner_url,
            bio,
            social_links,
            total_pieces_created
          )
        `)
        .not('mint_address', 'is', null)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error getting trending pieces:', error);
        return [];
      }

      // Batch fetch holder info
      const holderWallets = [...new Set(data.map(p => p.holder_wallet).filter(Boolean))];
      const holderMap = await batchGetHolderInfo(holderWallets);

      const enhanced = data.map(piece => ({
        ...piece,
        lowestPrice: piece.lowest_price,
        availableCount: piece.current_available_count,
        holder: piece.holder_wallet ? holderMap[piece.holder_wallet] : null
      }));

      // Normalize pieces to ensure proper URL generation from bucket/key
      return normalizePieces(enhanced);
    }

    // Get piece IDs from trending view
    const pieceIds = trendingData.map(t => t.piece_id);

    // Fetch full piece data directly from pieces table
    const { data: pieces, error: piecesError } = await supabase
      .from('pieces')
      .select(`
        *,
        social_links,
        creators (
          id,
          wallet,
          handle,
          is_verified,
          verification_type,
          profile_picture_url,
          banner_url,
          bio,
          social_links,
          total_pieces_created
        )
      `)
      .in('id', pieceIds)
      .not('mint_address', 'is', null);

    if (piecesError) {
      console.error('Error fetching piece details:', piecesError);
      return [];
    }

    // Batch fetch holder info
    const holderWallets = [...new Set(pieces.map(p => p.holder_wallet).filter(Boolean))];
    const holderMap = await batchGetHolderInfo(holderWallets);

    // Create a map for quick piece lookup
    const pieceMap = new Map(pieces.map(p => [p.id, p]));

    // Maintain trending order by mapping in the same order as trendingData
    const enhancedPieces = trendingData
      .map(trendingInfo => {
        const piece = pieceMap.get(trendingInfo.piece_id);
        if (!piece) return null;

        return {
          ...piece,
          lowestPrice: piece.lowest_price,
          availableCount: piece.current_available_count,
          views: trendingInfo?.total_views || 0,
          views_24h: trendingInfo?.views_24h || 0,
          views_7d: trendingInfo?.views_7d || 0,
          favorites_count: trendingInfo?.favorites_count || 0,
          trending_score: trendingInfo?.trending_score || 0,
          holder: piece.holder_wallet ? holderMap[piece.holder_wallet] : null
        };
      })
      .filter(Boolean); // Remove any null entries

    // Normalize pieces to ensure proper URL generation from bucket/key
    const normalizedPieces = normalizePieces(enhancedPieces);

    console.log('✅ Found', normalizedPieces.length, 'trending pieces');
    console.log('📊 Top trending piece:', normalizedPieces[0]?.name, 'Score:', normalizedPieces[0]?.trending_score?.toFixed(2));
    return normalizedPieces;

  } catch (error) {
    console.error('Error in getTrendingPieces:', error);
    return [];
  }
}

/**
 * Get master piece by ID with all edition information
 * OPTIMIZED: Uses marketplace_pieces view when possible
 * @param {number} pieceId - Piece ID
 * @returns {Promise<Object|null>} Master piece with edition data or null
 */
export async function getMasterPieceById(pieceId) {
  try {
    // First try marketplace_pieces view for better performance
    const { data: piece, error } = await supabase
      .from('marketplace_pieces')
      .select(`
        *,
        social_links,
        creators (
          id,
          wallet,
          handle,
          is_verified,
          verification_type,
          profile_picture_url,
          banner_url,
          bio,
          social_links,
          total_pieces_created
        )
      `)
      .eq('id', pieceId)
      .maybeSingle();

    if (error) {
      if (error.code === 'PGRST116') {
        // Not in marketplace_pieces, try regular pieces table
        const { data: regularPiece, error: regularError } = await supabase
          .from('pieces')
          .select(`
            *,
            creators (
              id,
              wallet,
              handle,
              is_verified,
              verification_type,
              profile_picture_url,
              banner_url,
              bio,
              social_links,
              total_pieces_created
            )
          `)
          .eq('id', pieceId)
          .maybeSingle();
        
        if (regularError) {
          return null;
        }

        // If this is an edition, get the master instead
        if (regularPiece.master_id) {
          console.log('🔄 Piece is an edition, fetching master:', regularPiece.master_id);
          return getMasterPieceById(regularPiece.master_id);
        }

        // Normalize the regular piece
        const [normalizedRegularPiece] = normalizePieces([regularPiece]);
        return normalizedRegularPiece;
      }
      throw error;
    }

    // Get all editions using RPC function
    const { data: allEditions } = await supabase.rpc('get_master_editions', {
      p_master_id: piece.id
    });

    // Get holder info
    const holder = piece.holder_wallet ? (await batchGetHolderInfo([piece.holder_wallet]))[piece.holder_wallet] : null;

    const enhancedPiece = {
      ...piece,
      lowestPrice: piece.lowest_price,
      availableCount: piece.current_available_count,
      editions: allEditions || [],
      holder
    };

    // Normalize piece to ensure proper URL generation from bucket/key
    const [normalizedPiece] = normalizePieces([enhancedPiece]);
    return normalizedPiece;

  } catch (error) {
    console.error('Error getting master piece by ID:', error);
    throw error;
  }
}

/**
 * Get all edition holders for a master piece
 * OPTIMIZED: Single query with join
 * @param {number} masterPieceId - The master piece ID
 * @returns {Promise<Array>} Array of holder information for each edition
 */
export async function getEditionHolders(masterPieceId) {
  try {
    console.log('🔍 getEditionHolders: Fetching holders for master piece:', masterPieceId);
    
    // Extract numeric ID from string like "supabase_113" or just "113"
    const numericId = typeof masterPieceId === 'string' && masterPieceId.includes('supabase_')
      ? parseInt(masterPieceId.replace('supabase_', ''))
      : parseInt(masterPieceId);
    
    console.log('🔢 Numeric piece ID:', numericId);
    
    if (isNaN(numericId)) {
      console.error('❌ Invalid piece ID:', masterPieceId);
      return [];
    }
    
    // First, check if this piece is an edition (has a master_id)
    const { data: thisPiece } = await supabase
      .from('pieces')
      .select('id, master_id, is_master, total_quantity')
      .eq('id', numericId)
      .single();
    
    console.log('📌 Current piece:', thisPiece);
    
    // Determine the actual master ID to query
    let actualMasterId = numericId;
    if (thisPiece?.master_id) {
      // This is an edition, use its master_id instead
      actualMasterId = thisPiece.master_id;
      console.log('🔄 Piece is an edition, using master_id:', actualMasterId);
    }
    
    // Get all editions for this master piece (includes master if it has edition_number)
    const { data: editions, error } = await supabase
      .from('pieces')
      .select(`
        id,
        edition_number,
        holder_wallet,
        mint_address,
        listing_status,
        listing_price,
        is_master,
        master_id
      `)
      .or(`id.eq.${actualMasterId},master_id.eq.${actualMasterId}`)
      .order('edition_number', { ascending: true });
    
    if (error) {
      console.error('❌ Error getting edition holders:', error);
      throw error;
    }
    
    console.log('📦 Raw editions data:', editions);
    
    // Filter editions with edition_number (true edition system)
    const validEditions = editions.filter(e => e.edition_number !== null && e.edition_number !== undefined);
    
    console.log('✅ Valid editions with edition_number:', validEditions.length);
    
    // If no editions with edition_number, handle pieces without proper edition numbering
    if (validEditions.length === 0) {
      console.log('⚠️ No editions with edition_number. Checking for pieces with holders...');
      
      // Get all pieces that have holders (whether it's a single piece or multiple editions without edition_number)
      const piecesWithHolders = editions.filter(e => e.holder_wallet);
      
      if (piecesWithHolders.length === 0) {
        console.warn('⚠️ No holders found. This piece might not be minted yet');
        return [];
      }
      
      console.log(`✅ Found ${piecesWithHolders.length} piece(s) with holders (no edition_number set)`);
      
      // Check for data inconsistency
      if (thisPiece?.total_quantity && piecesWithHolders.length < thisPiece.total_quantity) {
        console.warn(`⚠️ DATA INCONSISTENCY: Piece claims total_quantity=${thisPiece.total_quantity} but only found ${piecesWithHolders.length} pieces with holders`);
        console.warn(`   This might mean edition pieces weren't created properly or aren't linked via master_id`);
      }
      
      // Fetch holder profiles
      const holderWallets = [...new Set(piecesWithHolders.map(e => e.holder_wallet))];
      const holderMap = await batchGetHolderInfo(holderWallets);
      
      // Return holders with manually assigned edition numbers
      const holders = piecesWithHolders.map((piece, index) => ({
        editionNumber: index + 1, // Assign edition number based on order
        holderWallet: piece.holder_wallet,
        holderInfo: holderMap[piece.holder_wallet] || null,
        mintAddress: piece.mint_address,
        listingStatus: piece.listing_status,
        listingPrice: piece.listing_price,
        pieceId: piece.id // Include piece ID for debugging
      }));
      
      console.log(`✅ Returning ${holders.length} holder(s):`, holders.map(h => ({ edition: h.editionNumber, wallet: h.holderWallet?.slice(0,8), pieceId: h.pieceId })));
      return holders;
    }
    
    // Batch fetch holder profiles for edition system pieces
    const holderWallets = [...new Set(validEditions.map(e => e.holder_wallet).filter(Boolean))];
    const holderMap = await batchGetHolderInfo(holderWallets);
    
    console.log('👥 Unique holder wallets:', holderWallets.length);
    
    // Combine edition data with holder info
    const editionHolders = validEditions.map(edition => ({
      editionNumber: edition.edition_number,
      holderWallet: edition.holder_wallet,
      holderInfo: edition.holder_wallet ? holderMap[edition.holder_wallet] : null,
      mintAddress: edition.mint_address,
      listingStatus: edition.listing_status,
      listingPrice: edition.listing_price
    }));
    
    console.log('✅ Found', editionHolders.length, 'edition holders');
    
    return editionHolders;
    
  } catch (error) {
    console.error('❌ Error getting edition holders:', error);
    throw error;
  }
}

export default {
  getMarketplacePieces,
  getMarketplacePiecesByCategory,
  searchMarketplacePieces,
  getMarketplacePiecesByCreator,
  getTrendingPieces,
  getMasterPieceById,
  getEditionHolders
};
